import React from "react";
import {
  ChamberMemberCategory,
  ChamberMemberProAccountAllocationTypeEnum,
  CmpReportTypeEnum,
  GetCmpReportLookupsQuery,
} from "../../../graphql/types";
import CalendarDateInput from "../../common/dashboard/CalendarDateInput";
import {
  FormSelectField,
  FormTextField,
} from "../../common/dashboard/FormFields";
import { calendarDateFieldInputClassName } from "../../common/dashboard/formStyles";
import {
  ReportDefinition,
  ReportFilterKey,
} from "./reportDefinitions";

export type ReportFilterValues = {
  startDate: string;
  endDate: string;
  compareStartDate: string;
  compareEndDate: string;
  filterDate: string;
  membershipTypeId: string;
  memberSize: string;
  membershipCategory: ChamberMemberCategory | "";
  eventCode: string;
  eventId: string;
  eventTypeId: string;
  sponsorshipPackageId: string;
  ticketTypeId: string;
  advertisingCode: string;
  advertisingOptionId: string;
  leadCaptureId: string;
  createdByName: string;
  salesAccountNumber: string;
};

type ReportLookups = GetCmpReportLookupsQuery["getCmpReportLookups"];

export type ReportFiltersProps = {
  definition: ReportDefinition;
  lookups?: ReportLookups;
  lookupsLoading: boolean;
  values: ReportFilterValues;
  onChange: (values: ReportFilterValues) => void;
};

type LookupOption = {
  value: string;
  label: string;
};

const toSelectOptions = (options: readonly LookupOption[] | undefined) =>
  (options || []).map(({ value, label }) => ({ value, label }));

const allFilterValue = "__all_report_filter_values__";

const withAllOption = ({
  label,
  options,
}: {
  label: string;
  options: { value: string; label: React.ReactNode }[];
}) => [{ value: allFilterValue, label }, ...options];

const normalizeOptionalSelectValue = (value: string) =>
  value === allFilterValue ? "" : value;

const isMembershipCategory = (
  value: string,
): value is ChamberMemberCategory =>
  Object.values(ChamberMemberCategory).includes(
    value as ChamberMemberCategory,
  );

const getSalesAccountLabel = (
  type?: ChamberMemberProAccountAllocationTypeEnum,
) => {
  switch (type) {
    case ChamberMemberProAccountAllocationTypeEnum.EventTicketSales:
      return "Event Ticket Sales Account #";
    case ChamberMemberProAccountAllocationTypeEnum.SponsorshipSales:
      return "Sponsorship Sales Account #";
    case ChamberMemberProAccountAllocationTypeEnum.AdvertisingSales:
      return "Advertising Sales Account #";
    case ChamberMemberProAccountAllocationTypeEnum.CustomSales:
      return "Custom Sales Account #";
    case ChamberMemberProAccountAllocationTypeEnum.MembershipSales:
    default:
      return "Membership Sales Account #";
  }
};

const ReportDateField = ({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="min-w-0">
    <label
      htmlFor={id}
      className="mb-2 block text-sm font-bold text-gray-950"
    >
      {label}
    </label>
    <CalendarDateInput
      id={id}
      ariaLabel={label}
      mode="fullDate"
      value={value}
      onChange={onChange}
      placeholder="Select Date"
      iconPosition="right"
      inputClassName={calendarDateFieldInputClassName}
    />
  </div>
);

const ReportFilters = ({
  definition,
  lookups,
  lookupsLoading,
  values,
  onChange,
}: ReportFiltersProps) => {
  const update = (patch: Partial<ReportFilterValues>) =>
    onChange({ ...values, ...patch });
  const selectDisabled = lookupsLoading || !lookups;
  const salesAccountOptions = toSelectOptions(
    lookups?.salesAccounts.filter(
      ({ type }) => type === definition.salesAccountType,
    ),
  );
  const membershipTypeOptions =
    definition.reportType === CmpReportTypeEnum.MembersByEmployeeSize
      ? lookups?.employeeRangeMembershipTypes
      : lookups?.membershipTypes;

  const renderFilter = (filter: ReportFilterKey) => {
    switch (filter) {
      case "date":
        return (
          <ReportDateField
            key={filter}
            id="report-filter-date"
            label="Date"
            value={values.filterDate}
            onChange={(filterDate) => update({ filterDate })}
          />
        );
      case "membershipType":
        return (
          <FormSelectField
            key={filter}
            id="report-membership-type"
            label="Membership Type"
            value={values.membershipTypeId}
            onChange={(membershipTypeId) =>
              update({
                membershipTypeId:
                  normalizeOptionalSelectValue(membershipTypeId),
              })
            }
            options={withAllOption({
              label: "All Membership Types",
              options: toSelectOptions(membershipTypeOptions),
            })}
            placeholder={
              lookupsLoading
                ? "Loading Membership Types..."
                : "Select Membership Type"
            }
            disabled={selectDisabled}
            containerClassName="mb-0 min-w-0"
          />
        );
      case "membershipTier":
        return (
          <FormSelectField
            key={filter}
            id="report-membership-tier"
            label="Membership Tiers"
            value={values.membershipTypeId}
            onChange={(membershipTypeId) =>
              update({
                membershipTypeId:
                  normalizeOptionalSelectValue(membershipTypeId),
              })
            }
            options={withAllOption({
              label: "All Membership Tiers",
              options: toSelectOptions(lookups?.membershipTiers),
            })}
            placeholder={
              lookupsLoading
                ? "Loading Membership Tiers..."
                : "Select Membership Tier"
            }
            disabled={selectDisabled}
            containerClassName="mb-0 min-w-0"
          />
        );
      case "memberSize":
        return (
          <FormTextField
            key={filter}
            id="report-membership-size"
            label="Membership Size"
            value={values.memberSize}
            onChange={(memberSize) => update({ memberSize })}
            placeholder="Enter Membership Size"
            type="number"
            containerClassName="mb-0 min-w-0"
          />
        );
      case "membershipCategory":
        return (
          <FormSelectField
            key={filter}
            id="report-membership-category"
            label="Membership Category"
            value={values.membershipCategory}
            onChange={(membershipCategory) => {
              const normalizedCategory =
                normalizeOptionalSelectValue(membershipCategory);

              update({
                membershipCategory:
                  normalizedCategory && isMembershipCategory(normalizedCategory)
                    ? normalizedCategory
                    : "",
              });
            }}
            options={withAllOption({
              label: "All Membership Categories",
              options: toSelectOptions(lookups?.membershipCategories),
            })}
            placeholder={
              lookupsLoading
                ? "Loading Categories..."
                : "Select Membership Category"
            }
            disabled={selectDisabled}
            containerClassName="mb-0 min-w-0"
          />
        );
      case "eventCode":
        return (
          <FormSelectField
            key={filter}
            id="report-event-code"
            label="Event Code"
            value={values.eventCode}
            onChange={(eventCode) =>
              update({
                eventCode: normalizeOptionalSelectValue(eventCode),
              })
            }
            options={withAllOption({
              label: "All Event Codes",
              options: toSelectOptions(lookups?.eventCodes),
            })}
            placeholder={
              lookupsLoading ? "Loading Event Codes..." : "Select Event Code"
            }
            disabled={selectDisabled}
            containerClassName="mb-0 min-w-0"
          />
        );
      case "eventName":
        return (
          <FormSelectField
            key={filter}
            id="report-event-name"
            label="Event Name"
            value={values.eventId}
            onChange={(eventId) =>
              update({ eventId: normalizeOptionalSelectValue(eventId) })
            }
            options={withAllOption({
              label: "All Events",
              options: toSelectOptions(lookups?.events),
            })}
            placeholder={
              lookupsLoading ? "Loading Events..." : "Select Event Name"
            }
            disabled={selectDisabled}
            containerClassName="mb-0 min-w-0"
          />
        );
      case "eventType":
        return (
          <FormSelectField
            key={filter}
            id="report-event-type"
            label="Event Type"
            value={values.eventTypeId}
            onChange={(eventTypeId) =>
              update({
                eventTypeId: normalizeOptionalSelectValue(eventTypeId),
              })
            }
            options={withAllOption({
              label: "All Event Types",
              options: toSelectOptions(lookups?.eventTypes),
            })}
            placeholder={
              lookupsLoading ? "Loading Event Types..." : "Select Event Type"
            }
            disabled={selectDisabled}
            containerClassName="mb-0 min-w-0"
          />
        );
      case "sponsorshipLevel":
        return (
          <FormSelectField
            key={filter}
            id="report-sponsorship-level"
            label="Sponsorship Level"
            value={values.sponsorshipPackageId}
            onChange={(sponsorshipPackageId) =>
              update({
                sponsorshipPackageId:
                  normalizeOptionalSelectValue(sponsorshipPackageId),
              })
            }
            options={withAllOption({
              label: "All Sponsorship Levels",
              options: toSelectOptions(lookups?.sponsorshipLevels),
            })}
            placeholder={
              lookupsLoading
                ? "Loading Sponsorship Levels..."
                : "Select Sponsorship Level"
            }
            disabled={selectDisabled}
            containerClassName="mb-0 min-w-0"
          />
        );
      case "ticketType":
        return (
          <FormSelectField
            key={filter}
            id="report-ticket-type"
            label="Ticket Type"
            value={values.ticketTypeId}
            onChange={(ticketTypeId) =>
              update({
                ticketTypeId: normalizeOptionalSelectValue(ticketTypeId),
              })
            }
            options={withAllOption({
              label: "All Ticket Types",
              options: toSelectOptions(lookups?.ticketTypes),
            })}
            placeholder={
              lookupsLoading ? "Loading Ticket Types..." : "Select Ticket Type"
            }
            disabled={selectDisabled}
            containerClassName="mb-0 min-w-0"
          />
        );
      case "advertisingCode":
        return (
          <FormSelectField
            key={filter}
            id="report-advertising-code"
            label="Advertising Code"
            value={values.advertisingCode}
            onChange={(advertisingCode) =>
              update({
                advertisingCode:
                  normalizeOptionalSelectValue(advertisingCode),
              })
            }
            options={withAllOption({
              label: "All Advertising Codes",
              options: toSelectOptions(lookups?.advertisingCodes),
            })}
            placeholder={
              lookupsLoading
                ? "Loading Advertising Codes..."
                : "Select Advertising Code"
            }
            disabled={selectDisabled}
            containerClassName="mb-0 min-w-0"
          />
        );
      case "advertisingOptions":
        return (
          <FormSelectField
            key={filter}
            id="report-advertising-option"
            label="Advertising Options"
            value={values.advertisingOptionId}
            onChange={(advertisingOptionId) =>
              update({
                advertisingOptionId:
                  normalizeOptionalSelectValue(advertisingOptionId),
              })
            }
            options={withAllOption({
              label: "All Advertising Options",
              options: toSelectOptions(lookups?.advertisingOptions),
            })}
            placeholder={
              lookupsLoading
                ? "Loading Advertising Options..."
                : "Select Advertising Options"
            }
            disabled={selectDisabled}
            containerClassName="mb-0 min-w-0"
          />
        );
      case "leadCapture":
        return (
          <FormSelectField
            key={filter}
            id="report-lead-capture"
            label="Lead Capture"
            value={values.leadCaptureId}
            onChange={(leadCaptureId) =>
              update({
                leadCaptureId: normalizeOptionalSelectValue(leadCaptureId),
              })
            }
            options={withAllOption({
              label: "All Lead Captures",
              options: toSelectOptions(lookups?.prospectLeadCaptures),
            })}
            placeholder={
              lookupsLoading
                ? "Loading Lead Captures..."
                : "Select Lead Capture"
            }
            disabled={selectDisabled}
            containerClassName="mb-0 min-w-0"
          />
        );
      case "createdBy":
        return (
          <FormSelectField
            key={filter}
            id="report-lead-created-by"
            label="Lead Created By"
            value={values.createdByName}
            onChange={(createdByName) =>
              update({
                createdByName: normalizeOptionalSelectValue(createdByName),
              })
            }
            options={withAllOption({
              label: "All Lead Creators",
              options: toSelectOptions(lookups?.prospectCreatedByNames),
            })}
            placeholder={
              lookupsLoading
                ? "Loading Lead Creators..."
                : "Select Lead Created By"
            }
            disabled={selectDisabled}
            containerClassName="mb-0 min-w-0"
          />
        );
      case "salesAccount":
        return (
          <FormSelectField
            key={filter}
            id="report-sales-account"
            label={getSalesAccountLabel(definition.salesAccountType)}
            value={values.salesAccountNumber}
            onChange={(salesAccountNumber) =>
              update({
                salesAccountNumber:
                  normalizeOptionalSelectValue(salesAccountNumber),
              })
            }
            options={withAllOption({
              label: "All Sales Accounts",
              options: salesAccountOptions,
            })}
            placeholder={
              lookupsLoading
                ? "Loading Sales Accounts..."
                : "Select Sales Account #"
            }
            disabled={selectDisabled}
            containerClassName="mb-0 min-w-0"
          />
        );
    }
  };

  return (
    <div className="min-w-0">
      <div className="grid grid-cols-1 gap-x-7 gap-y-7 sm:grid-cols-2">
        <ReportDateField
          id="report-start-date"
          label="Report Start Date"
          value={values.startDate}
          onChange={(startDate) => update({ startDate })}
        />
        <ReportDateField
          id="report-end-date"
          label="Report End Date"
          value={values.endDate}
          onChange={(endDate) => update({ endDate })}
        />
        <ReportDateField
          id="compare-start-date"
          label="Compare To Start Date"
          value={values.compareStartDate}
          onChange={(compareStartDate) => update({ compareStartDate })}
        />
        <ReportDateField
          id="compare-end-date"
          label="Compare To End Date"
          value={values.compareEndDate}
          onChange={(compareEndDate) => update({ compareEndDate })}
        />
      </div>

      {definition.filters.length > 0 && (
        <div className="mt-7">
          <h2 className="mb-4 text-base font-bold text-gray-950">Filters</h2>
          <div className="grid grid-cols-1 gap-5">
            {definition.filters.map(renderFilter)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportFilters;
